// 네이버 CAPA 화면 계산 — 색 규칙·상태 배지·차수 나누기·센터 타일·위험 예상을 한 곳에 둔다(화면은 그리기만).
//
// 🔴 서버 시각은 UTC naive('Z' 없는 ISO)다 — 비교·표시 모두 kstTime.parseServerUtc 로만 읽는다.
//    `Date.parse(v)`·`new Date(v)` 는 'Z' 없는 ISO 를 로컬 시각으로 읽어 KST 브라우저에서 9시간 어긋난다.
// 🔴 시각 표시는 timeZone 'Asia/Seoul' + hourCycle 'h23' — `getHours()` 는 실행 환경 시간대를 따르고
//    `hour12: false` 는 자정 직후를 '24:05' 로 낸다.
// 🔴 현재 시각이 필요한 함수는 nowMs 를 인자로 받는다(시험이 시각을 고정한다).
import { fmtKstDateTime, parseServerUtc } from '../../utils/kstTime';

export const DASH = '—';
const MINUTE_MS = 60 * 1000;
const KST_OFFSET_MS = 9 * 60 * MINUTE_MS;
// 위험 예상 칸에 띄우는 범위(분) — 그보다 먼 외삽은 예보로 띄우지 않는다.
export const RISK_WINDOW_MINUTES = 240;
// 서버 interval_seconds 가 없을 때의 수집 주기(초).
const DEFAULT_INTERVAL_SECONDS = 120;
export const TOTAL_TILE_KEY = '__total__';

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
const isInt = (v) => Number.isInteger(v);
// 수집기와 같은 «판정할 수 있는 행»: available 이 정수이고 total 이 0·null 이 아니다(capa_rules.find_low_capa_rows).
const isKnownRow = (row) => !!row && isInt(row.available_capa) && isNum(row.total_capa) && row.total_capa !== 0;

function ratios(display) {
  if (!display || !isNum(display.threshold_ratio) || !isNum(display.warning_ratio)) return null;
  return { threshold: display.threshold_ratio, warning: display.warning_ratio };
}

// ── 시각 ────────────────────────────────────────────────────────────────
const KST_CLOCK = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Seoul',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

/** 서버 시각(또는 Date) → KST 'HH:MM'(seconds 면 'HH:MM:SS'). 해석 불가면 ''. */
export function kstClock(value, { seconds = false } = {}) {
  const date = value instanceof Date ? value : parseServerUtc(value);
  if (!date || Number.isNaN(date.getTime())) return '';
  const p = {};
  KST_CLOCK.formatToParts(date).forEach(({ type, value: part }) => { p[type] = part; });
  if (!p.hour || !p.minute) return '';
  return seconds ? `${p.hour}:${p.minute}:${p.second || '00'}` : `${p.hour}:${p.minute}`;
}

/** eta_at(UTC naive) → KST 'HH:MM'. */
export const etaClock = (etaAt) => kstClock(etaAt);

/** nowMs → KST 자정부터 분(실행 환경 시간대와 무관). */
export function kstMinutes(nowMs) {
  const d = new Date(nowMs + KST_OFFSET_MS);
  return d.getUTCHours() * 60 + d.getUTCMinutes();
}

/** 'HH:MM' → 자정부터 분 · '24:00' = 1440 · 못 읽으면 fallback(수집기 capa_rules.hhmm_minutes 와 같다). */
export function hhmmMinutes(value, fallback) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(value ?? ''));
  return m ? Number(m[1]) * 60 + Number(m[2]) : fallback;
}

/** 수집 시간대 안 — start ≤ m < end(수집기 capa_rules.in_active_window 와 같은 규칙). */
export function inActiveWindow(activeStart, activeEnd, nowMs) {
  const m = kstMinutes(nowMs);
  return hhmmMinutes(activeStart, 0) <= m && m < hhmmMinutes(activeEnd, 24 * 60);
}

// ── 색 · 상태 ──────────────────────────────────────────────────────────────
/**
 * 행 하나의 색 — 'ok' | 'warn' | 'danger' | 'unknown'.
 * 🔴 식은 수집기 알림(capa_rules.py `available <= total * float(ratio)`)과 글자 그대로 같게 쓴다(EPS 보정 없음).
 */
export function levelOf(row, display) {
  const r = ratios(display);
  if (!r || !isKnownRow(row)) return 'unknown';
  const total = row.total_capa;
  const available = row.available_capa;
  if (available <= total * r.threshold) return 'danger';
  if (available <= total * r.warning) return 'warn';
  return 'ok';
}

/**
 * 상태 배지 — 'live' | 'late' | 'stopped' | 'login' | 'idle' | 'off' | 'unknown'(판정 순서 = 이 순서).
 * 로그인 판정은 error_kind 가 아니라 latest.snapshot.status 로 한다(login_rate_limited 는 서버가 «살아 있음»으로 센다).
 */
export function freshness(status, latest, nowMs) {
  if (!status) return 'unknown';
  if (!status.enabled) return 'off';
  const snapshotStatus = latest?.snapshot?.status;
  if (status.credentials?.set === false || snapshotStatus === 'login_failed' || snapshotStatus === 'login_required') {
    return 'login';
  }
  if (status.collector_down) return 'stopped';
  if (!inActiveWindow(status.active_start, status.active_end, nowMs)) return 'idle';
  const lastOk = parseServerUtc(status.last_ok_at);
  const interval = isNum(status.interval_seconds) && status.interval_seconds > 0
    ? status.interval_seconds : DEFAULT_INTERVAL_SECONDS;
  if (!lastOk || nowMs - lastOk.getTime() > interval * 1.5 * 1000) return 'late';
  return 'live';
}

export const FRESHNESS_LABELS = {
  live: '정상', late: '지연', stopped: '정지', login: '로그인 필요', idle: '수집 시간 외', off: '꺼짐', unknown: '상태 모름',
};

// ── 차수 · 타일 ────────────────────────────────────────────────────────────
/**
 * 진행 중(degree === currentDegree) · 예정(degree > currentDegree). 지난 차수는 어느 쪽에도 넣지 않는다.
 * currentDegree 는 행과 같은 스냅샷인 latest.ok_snapshot.current_degree 를 넘긴다 · null 이면 전부 예정.
 */
export function splitByDegree(rows, currentDegree) {
  const list = Array.isArray(rows) ? rows : [];
  if (!isInt(currentDegree)) return { current: [], upcoming: list.slice() };
  return {
    current: list.filter((row) => row.degree === currentDegree),
    upcoming: list.filter((row) => isNum(row.degree) && row.degree > currentDegree),
  };
}

function riskInfo(row, display, fresh, nowMs) {
  const none = { text: DASH, d: null };
  const level = levelOf(row, display);
  if (level === 'unknown') return none;
  if (level === 'danger') return { text: '위험 구간', d: null };
  if (fresh !== 'live' && fresh !== 'late') return none;
  if (!row.eta_at) return none;
  const t = parseServerUtc(row.eta_at);
  if (!t) return none;
  const d = t.getTime() - nowMs;
  if (d >= 0 && d <= RISK_WINDOW_MINUTES * MINUTE_MS) return { text: `${kstClock(t)}경 위험 예상`, d };
  if (d < 0 && fresh === 'live') return { text: `곧 위험(${kstClock(t)} 예상)`, d };
  return none;
}

/** 행 하나의 위험 예상 칸(표·타일 공통). eta_minutes 는 쓰지 않는다 — eta_at 과 지금 시각으로 다시 잰다. */
export function riskText(row, display, fresh, nowMs) {
  return riskInfo(row, display, fresh, nowMs).text;
}

function tileRisk(rows, display, fresh, nowMs) {
  const dangerCount = rows.filter((row) => levelOf(row, display) === 'danger').length;
  if (dangerCount > 0) return `위험 구간 ${dangerCount}건`;
  let best = null;
  rows.forEach((row) => {
    const info = riskInfo(row, display, fresh, nowMs);
    if (isNum(info.d) && (best === null || info.d < best.d)) best = info;
  });
  return best ? best.text : '';
}

function makeTile(key, name, rows, display, fresh, nowMs) {
  const known = rows.filter(isKnownRow);
  let total = null;
  let available = null;
  let ratio = null;
  let level = 'unknown';
  if (known.length > 0) {
    total = known.reduce((sum, row) => sum + row.total_capa, 0);
    available = known.reduce((sum, row) => sum + row.available_capa, 0);
    ratio = total ? available / total : null;
    level = levelOf({ total_capa: total, available_capa: available }, display);
  }
  return { key, name, total, available, ratio, level, risk: tileRisk(rows, display, fresh, nowMs), rowCount: rows.length };
}

/**
 * 센터 타일 — 맨 앞 Total + display.centers 순서(목록에 없는 센터는 뒤 · 목록이 비면 행 순서).
 * rows = 진행 중 차수 행. 진행 중 행이 0개면 빈 배열(화면이 «진행 중 차수 행이 없습니다»를 그린다).
 */
export function centerTiles(rows, display, fresh, nowMs) {
  const list = Array.isArray(rows) ? rows : [];
  if (list.length === 0) return [];
  const centers = Array.isArray(display?.centers) ? display.centers.filter((c) => typeof c === 'string') : [];
  const names = [...centers];
  list.forEach((row) => { if (!names.includes(row.center_name)) names.push(row.center_name); });
  const tiles = [makeTile(TOTAL_TILE_KEY, 'Total', list, display, fresh, nowMs)];
  names.forEach((name) => {
    tiles.push(makeTile(name, name, list.filter((row) => row.center_name === name), display, fresh, nowMs));
  });
  return tiles;
}

// ── 칸 ──────────────────────────────────────────────────────────────────
/** 위험선(개) — 가용이 이 수 이하면 위험. 계산할 수 없으면 null. */
export function criticalQty(row, display) {
  const r = ratios(display);
  if (!r || !row || !isNum(row.total_capa) || row.total_capa === 0) return null;
  return Math.floor(row.total_capa * r.threshold);
}

/** 소진 속도 — 수집기 rate = change / minutes 라 줄어들 때 음수다. 음수일 때만 «분당 N개 소진». */
export function burnText(row) {
  const rate = row?.rate_per_min;
  if (!isNum(rate) || rate >= 0) return '';
  return `분당 ${Math.abs(rate)}개 소진`;
}

/** 가용률(0~1) — 판정할 수 없는 행이면 null. */
export function ratioOf(row) {
  return isKnownRow(row) ? row.available_capa / row.total_capa : null;
}

/** 0.2 → '20%' · 0.255 → '25.5%'. */
export function percentText(ratio) {
  if (!isNum(ratio)) return DASH;
  return `${Math.round(ratio * 1000) / 10}%`;
}

export function countText(value) {
  return isNum(value) ? value.toLocaleString('ko-KR') : DASH;
}

// ── 사람 말 ─────────────────────────────────────────────────────────────────
const REGION_SOURCE_LABELS = {
  auto_fallback: '자동 대체', override: '수동 지정', holiday_eve: '휴일 전날', weekday: '평일', weekend: '주말',
};
export const regionSourceText = (source) => (source ? (REGION_SOURCE_LABELS[source] || String(source)) : '');

const SNAPSHOT_STATUS_LABELS = {
  ok: '정상', incomplete: '일부 누락', login_required: '로그인 필요', login_failed: '로그인 실패', error: '오류', skipped: '건너뜀',
};
export const snapshotStatusText = (status) => (status ? (SNAPSHOT_STATUS_LABELS[status] || String(status)) : DASH);

const EVENT_KIND_LABELS = {
  low_capa: '가용 부족', empty_degree: '빈 차수', region_fallback: '권역 자동 대체', login_required: '로그인 필요',
};
/** 알림 대상이 받는 사건 종류 이름 — 체크박스·사건 종류 칸·감사 글이 같이 쓴다. */
export const NOTIFY_KIND_LABELS = { ...EVENT_KIND_LABELS, collector_down: '수집기 이상' };
/** 사건 종류 순서 = 서버 EVENT_KINDS(models/capa.py). 보내는 kinds 는 늘 이 순서로 담는다. */
export const NOTIFY_KINDS = ['low_capa', 'empty_degree', 'region_fallback', 'login_required', 'collector_down'];
const COLLECTOR_STATE_LABELS = { down: '수집기 이상', up: '수집기 회복', suspended: '판정 쉼' };

/** 사건 종류 — collector_down 은 payload.state 로 가른다 · 모르는 값은 원문. */
export function eventText(event) {
  const kind = event?.kind;
  if (kind === 'collector_down') {
    const state = event?.payload?.state;
    return COLLECTOR_STATE_LABELS[state] || (state ? `${kind}(${state})` : String(kind));
  }
  return EVENT_KIND_LABELS[kind] || String(kind ?? DASH);
}

// ── 경고음 판정 ───────────────────────────────────────────────────────────────
/** capa_id → level(진행 중 차수 행만). */
export function levelMap(rows, display) {
  const out = {};
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (row && row.capa_id) out[row.capa_id] = levelOf(row, display);
  });
  return out;
}

/**
 * 새로 danger·warn 이 된 행 수 — 이전에 danger 가 아니었던 행이 danger 면 새 danger,
 * 이전에 warn·danger 가 아니었던 행이 warn 이면 새 warn. unknown 은 세지 않는다.
 */
export function alarmChanges(prevLevels, rows, display) {
  const prev = prevLevels || {};
  const levels = levelMap(rows, display);
  let newDanger = 0;
  let newWarn = 0;
  Object.entries(levels).forEach(([id, level]) => {
    if (level === 'danger' && prev[id] !== 'danger') newDanger += 1;
    else if (level === 'warn' && prev[id] !== 'warn' && prev[id] !== 'danger') newWarn += 1;
  });
  return { levels, newDanger, newWarn };
}

// ── 오류 문구 ──────────────────────────────────────────────────────────────
/** 응답 오류(JSON 본문) → 화면 문구. 503 storage_not_ready 는 고정 문구. */
export function detailText(detail, fallback) {
  if (detail && typeof detail === 'object') {
    if (detail.code === 'storage_not_ready') return '저장소 준비 중입니다(관리자 확인 필요)';
    if (detail.code === 'range_too_long') return '최대 31일까지 내려받을 수 있습니다';
    if (typeof detail.message === 'string' && detail.message) return detail.message;
  }
  if (typeof detail === 'string' && detail) return detail;
  return fallback;
}

export function errorText(err, fallback) {
  return detailText(err?.response?.data?.detail, fallback);
}

// ── 관리 탭 설정(P2b) ───────────────────────────────────────────────────────────
// 서버 검사(backend/routers/capa.py _validated_settings)와 같은 규칙을 화면이 먼저 본다 — 서버 값 ↔ 폼 글자.
export const SETTINGS_FIELDS = [
  'active_start', 'active_end', 'centers', 'region_group', 'region_weekday', 'region_weekend',
  'region_override_dates', 'business_day_overrides', 'threshold_ratio', 'warning_ratio',
  'holiday_eve_min_streak', 'empty_degree_alert_minutes', 'alert_max_age_minutes', 'headless',
];

/** 칸 화면 이름 — 입력칸 label 과 감사 기록 글이 같이 쓴다. */
export const SETTINGS_LABELS = {
  active_start: '수집 시작',
  active_end: '수집 끝',
  centers: '센터',
  region_group: '권역 묶음',
  region_weekday: '평일 권역',
  region_weekend: '주말 권역',
  region_override_dates: '날짜별 권역',
  business_day_overrides: '영업일 예외',
  threshold_ratio: '위험선(%)',
  warning_ratio: '주의선(%)',
  holiday_eve_min_streak: '연휴 전날 최소 연속',
  empty_degree_alert_minutes: '0건 경고(분)',
  alert_max_age_minutes: '알림 유효(분)',
  headless: '헤드리스',
};

const CENTERS_MAX = 20;
const DAY_MAP_MAX = 366;
const TEXT_MAX = 100;
const HHMM_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
// 비율 칸 — 소수 첫째 자리까지만(반올림하지 않는다).
const PCT_RE = /^\d{1,3}(\.\d)?$/;
const INT_RE = /^\d+$/;
const TEXT_FIELDS = ['region_group', 'region_weekday', 'region_weekend'];
const RATIO_FIELDS = ['threshold_ratio', 'warning_ratio'];
// [최소, 최대] 0.1% 단위(위험선 0.1~99.9 · 주의선 0.1~100).
const RATIO_TENTHS = { threshold_ratio: [1, 999], warning_ratio: [1, 1000] };
const INT_RANGES = { holiday_eve_min_streak: [1, 10], empty_degree_alert_minutes: [1, 120], alert_max_age_minutes: [1, 1440] };
const DAY_MAP_KIND = { region_override_dates: 'region', business_day_overrides: 'business' };
const has = (obj, key) => !!obj && Object.prototype.hasOwnProperty.call(obj, key);

function isRealDay(text) {
  const m = DAY_RE.exec(text);
  if (!m) return false;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (y < 1) return false;
  const dt = new Date(0);
  dt.setUTCFullYear(y, mo - 1, d);
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === mo - 1 && dt.getUTCDate() === d;
}

// 줄마다 \r·앞뒤 공백을 지우고 빈 줄은 버린다 · line 은 원래 줄 번호(1부터).
function cleanLines(text) {
  const out = [];
  String(text ?? '').split('\n').forEach((raw, i) => {
    const t = raw.replace(/\r/g, '').trim();
    if (t) out.push({ line: i + 1, text: t });
  });
  return out;
}

/** 센터 textarea(한 줄에 하나) → {value: 배열, errors: [{line, message}]}. 1~20개 · 각 1~100자 · 중복 금지. */
export function parseCenters(text) {
  const lines = cleanLines(text);
  const value = [];
  const errors = [];
  if (lines.length === 0) errors.push({ line: null, message: '센터를 1개 이상 적으세요' });
  if (lines.length > CENTERS_MAX) errors.push({ line: null, message: `센터는 ${CENTERS_MAX}개까지입니다` });
  lines.forEach(({ line, text: name }) => {
    if (name.length > TEXT_MAX) {
      errors.push({ line, message: `${TEXT_MAX}자까지입니다` });
    } else if (value.includes(name)) {
      errors.push({ line, message: '앞 줄과 겹칩니다' });
    } else {
      value.push(name);
    }
  });
  return { value, errors };
}

/**
 * 날짜 textarea → {value: {날짜: 값}, errors: [{line, message}]}.
 * 한 줄 = 첫 공백문자 앞이 날짜(실제 있는 날) · 뒤 전부(앞뒤 공백 제거)가 값.
 * kind 'region' = 권역 1~100자(공백 포함 가능) · 'business' = «영업»→true · «휴무»→false. 같은 날짜 두 번·366줄 초과는 오류.
 */
export function parseDayMap(text, kind) {
  const lines = cleanLines(text);
  const value = {};
  const errors = [];
  const seen = new Set();
  if (lines.length > DAY_MAP_MAX) errors.push({ line: null, message: `${DAY_MAP_MAX}줄까지입니다` });
  lines.forEach(({ line, text: t }) => {
    const cut = t.search(/\s/);
    if (cut < 0) {
      errors.push({ line, message: kind === 'business' ? '«YYYY-MM-DD 영업» 또는 «YYYY-MM-DD 휴무»로 적으세요' : '«YYYY-MM-DD 권역»으로 적으세요' });
      return;
    }
    const day = t.slice(0, cut);
    const rest = t.slice(cut + 1).trim();
    if (!isRealDay(day)) {
      errors.push({ line, message: '날짜는 실제 있는 날(YYYY-MM-DD)이어야 합니다' });
      return;
    }
    if (seen.has(day)) {
      errors.push({ line, message: '같은 날짜가 앞 줄에 있습니다' });
      return;
    }
    seen.add(day);
    if (kind === 'business') {
      if (rest === '영업') value[day] = true;
      else if (rest === '휴무') value[day] = false;
      else errors.push({ line, message: '값은 «영업» 또는 «휴무»여야 합니다' });
      return;
    }
    if (!rest || rest.length > TEXT_MAX) {
      errors.push({ line, message: `권역은 1~${TEXT_MAX}자여야 합니다` });
      return;
    }
    value[day] = rest;
  });
  return { value, errors };
}

/** {날짜: 값} → 날짜 오름차순 줄 글자(business 는 true→«영업» · false→«휴무»). */
export function formatDayMap(obj, kind) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return '';
  return Object.keys(obj).sort().map((day) => {
    const v = obj[day];
    let text;
    if (kind === 'business') text = v === true ? '영업' : (v === false ? '휴무' : String(v));
    else text = String(v ?? '');
    return `${day} ${text}`;
  }).join('\n');
}

/** '12.5' → 0.125 · 형식 오류(빈 칸·«.5»·«1e1»·소수 둘째 자리)면 null. 범위는 보지 않는다. */
export function pctToRatio(text) {
  const t = String(text ?? '').trim();
  if (!PCT_RE.test(t)) return null;
  return Math.round(Number(t) * 10) / 1000;
}

/** 0.125 → '12.5' · 0.2 → '20' · 0.29 → '29'(String(r*100) 은 0.29 에서 소수 꼬리가 붙는다). 수가 아니면 ''. */
export function ratioToPct(ratio) {
  if (!isNum(ratio)) return '';
  return String(Math.round(ratio * 1000) / 10);
}

/** 서버 설정 → 폼 글자(헤드리스만 bool). settings 가 없으면 null. */
export function settingsDraft(settings) {
  if (!settings || typeof settings !== 'object') return null;
  const draft = {};
  SETTINGS_FIELDS.forEach((f) => {
    const v = settings[f];
    if (f === 'headless') draft[f] = !!v;
    else if (f === 'centers') draft[f] = Array.isArray(v) ? v.join('\n') : '';
    else if (has(DAY_MAP_KIND, f)) draft[f] = formatDayMap(v, DAY_MAP_KIND[f]);
    else if (RATIO_FIELDS.includes(f)) draft[f] = ratioToPct(v);
    else draft[f] = v === null || v === undefined ? '' : String(v);
  });
  return draft;
}

/** 폼 글자 → {value: 서버 값(검사를 지난 칸만), errors: [{field, line, message}]}. 둘이 엮인 칸(시작<끝 · 위험선<주의선)도 본다. */
export function toServer(draft) {
  const d = draft || {};
  const value = {};
  const errors = [];
  const fail = (field, message, line = null) => errors.push({ field, line, message });

  ['active_start', 'active_end'].forEach((f) => {
    const t = String(d[f] ?? '').trim();
    if (HHMM_RE.test(t) || (f === 'active_end' && t === '24:00')) value[f] = t;
    else fail(f, f === 'active_end' ? 'HH:MM(00:00~23:59) 또는 24:00 으로 적으세요' : 'HH:MM(00:00~23:59)으로 적으세요');
  });
  if (has(value, 'active_start') && has(value, 'active_end')
      && hhmmMinutes(value.active_start, 0) >= hhmmMinutes(value.active_end, 24 * 60)) {
    fail('active_end', '시작이 끝보다 앞서야 합니다(같은 날 안)');
  }

  const centers = parseCenters(d.centers);
  if (centers.errors.length) centers.errors.forEach((e) => fail('centers', e.message, e.line));
  else value.centers = centers.value;

  TEXT_FIELDS.forEach((f) => {
    const t = String(d[f] ?? '').trim();
    if (!t || t.length > TEXT_MAX) fail(f, `1~${TEXT_MAX}자로 적으세요`);
    else value[f] = t;
  });

  Object.keys(DAY_MAP_KIND).forEach((f) => {
    const parsed = parseDayMap(d[f], DAY_MAP_KIND[f]);
    if (parsed.errors.length) parsed.errors.forEach((e) => fail(f, e.message, e.line));
    else value[f] = parsed.value;
  });

  const tenths = {};
  RATIO_FIELDS.forEach((f) => {
    const t = String(d[f] ?? '').trim();
    const [low, high] = RATIO_TENTHS[f];
    if (!t) { fail(f, '값을 적으세요'); return; }
    if (!PCT_RE.test(t)) { fail(f, '숫자(소수 첫째 자리까지)로 적으세요'); return; }
    const n = Math.round(Number(t) * 10);
    if (n < low || n > high) { fail(f, `${low / 10}~${high / 10} 사이여야 합니다`); return; }
    tenths[f] = n;
    value[f] = n / 1000;
  });
  if (has(tenths, 'threshold_ratio') && has(tenths, 'warning_ratio') && !(tenths.threshold_ratio < tenths.warning_ratio)) {
    fail('warning_ratio', '주의선은 위험선보다 커야 합니다');
  }

  Object.keys(INT_RANGES).forEach((f) => {
    const t = String(d[f] ?? '').trim();
    const [low, high] = INT_RANGES[f];
    if (!INT_RE.test(t)) { fail(f, '정수로 적으세요'); return; }
    const n = Number(t);
    if (n < low || n > high) { fail(f, `${low}~${high} 사이 정수여야 합니다`); return; }
    value[f] = n;
  });

  value.headless = !!d.headless;
  return { value, errors };
}

// 배열·객체는 객체 키를 정렬해 비교한다(서버는 응답마다 새 배열·키 순서를 준다).
function canonical(v) {
  if (Array.isArray(v)) return v.map(canonical);
  if (v && typeof v === 'object') {
    const out = {};
    Object.keys(v).sort().forEach((k) => { out[k] = canonical(v[k]); });
    return out;
  }
  return v;
}

/** 같은 값인가 — 원시값은 ===, 배열·객체는 키 정렬 뒤 JSON 비교. */
export function sameValue(a, b) {
  if (a === b) return true;
  try {
    return JSON.stringify(canonical(a)) === JSON.stringify(canonical(b));
  } catch (e) {
    return false;
  }
}

/** 저장 판단 — draft 를 서버 값으로 바꾼 뒤 loaded 와 다른 칸만 changes · 화면 검사 오류는 errors. */
export function settingsDiff(loaded, draft) {
  const { value, errors } = toServer(draft);
  const changes = {};
  SETTINGS_FIELDS.forEach((f) => {
    if (has(value, f) && !sameValue(value[f], loaded ? loaded[f] : undefined)) changes[f] = value[f];
  });
  return { changes, errors };
}

/**
 * 새로 받은 설정(next)을 편집 중인 폼에 합친다 → {loaded: next, draft, conflicts}.
 * «내가 고친 칸» = draft 글자가 settingsDraft(loaded) 와 다른 칸(형식 오류 칸도 포함) — draft 에 남긴다.
 * 나머지 칸은 next 값으로 덮는다. conflicts = 내가 고친 칸 중 남이 loaded 와 다른 값으로 바꿨고 그 값이 내 값과도 다른 칸.
 */
export function mergeSettings(loaded, draft, next) {
  const nextDraft = settingsDraft(next);
  if (!loaded || !draft) return { loaded: next, draft: nextDraft, conflicts: [] };
  const base = settingsDraft(loaded);
  const mine = toServer(draft).value;
  const merged = { ...nextDraft };
  const conflicts = [];
  SETTINGS_FIELDS.forEach((f) => {
    if (draft[f] === base[f]) return;
    merged[f] = draft[f];
    const theirs = next ? next[f] : undefined;
    if (sameValue(theirs, loaded[f])) return;
    if (has(mine, f) && sameValue(theirs, mine[f])) return;
    conflicts.push(f);
  });
  return { loaded: next, draft: merged, conflicts };
}

/** 설정 값 한 칸 → 사람 글(비율 %·bool 켬/끔·날짜 맵 « / »·센터 «, »). max 가 있으면 넘는 만큼 자르고 «…». */
export function fmtSettingValue(field, value, max) {
  let text;
  if (value === null || value === undefined) text = DASH;
  else if (RATIO_FIELDS.includes(field) && isNum(value)) text = `${ratioToPct(value)}%`;
  else if (typeof value === 'boolean') text = value ? '켬' : '끔';
  else if (has(DAY_MAP_KIND, field)) text = formatDayMap(value, DAY_MAP_KIND[field]).split('\n').join(' / ') || DASH;
  else if (Array.isArray(value)) text = value.join(', ');
  else text = String(value);
  if (Number.isInteger(max) && max > 0 && text.length > max) return `${text.slice(0, max)}…`;
  return text;
}

// ── 알림 대상(P3b) ─────────────────────────────────────────────────────────────
const TARGET_KIND_LABELS = { mm_user: '메신저 DM', email: '메일', mm_channel: '채널' };
// 서버 OUTBOX_STATUSES(models/capa.py) — 마지막 발송 상태.
const OUTBOX_STATUS_LABELS = { pending: '대기', sent: '보냄', failed: '실패', expired: '만료', skipped: '건너뜀' };
// 서버 ERROR_CODES(services/capa_notify.py · 10개) — 발송 실패·건너뜀 사유.
const SEND_ERROR_LABELS = {
  dm_failed: '메신저 DM 실패',
  mail_failed: '메일 실패',
  channel_failed: '채널 글 실패',
  mm_lookup_failed: '메신저 사용자 조회 실패',
  send_error: '보내기 오류',
  target_off: '대상 꺼짐',
  inactive: '비활성 계정',
  no_menu: 'CAPA 메뉴 권한 없음',
  no_mm_user: '메신저 계정 없음',
  too_old: '너무 늦어 안 보냄',
};
// 감사 «알림 대상 수정»에 그리는 칸 이름(값은 그리지 않는다).
const TARGET_FIELD_LABELS = { label: '이름표', kinds: '사건 종류', enabled: '켜짐' };

const targetKindText = (kind) => TARGET_KIND_LABELS[kind] || String(kind ?? DASH);

/** 사건 종류 배열 → «가용 부족, 빈 차수» · 빈 배열·배열 아님이면 —. */
export function kindsText(kinds) {
  if (!Array.isArray(kinds) || kinds.length === 0) return DASH;
  return kinds.map((k) => NOTIFY_KIND_LABELS[k] || String(k)).join(', ');
}

/** 알림 대상 1행 → {who, sub}(표의 «대상» 칸). 이름표가 있으면 who 뒤에 «(이름표)». */
export function targetText(target) {
  const kind = target?.kind;
  let who;
  let sub;
  if (kind === 'mm_user' || kind === 'email') {
    who = `${TARGET_KIND_LABELS[kind]} · ${target.user_name || DASH}`;
    sub = target.email_hint || DASH;
  } else if (kind === 'mm_channel') {
    who = `채널 · ${target.channel_label || DASH}`;
    sub = target.channel_type === 'P' ? '비공개' : '공개';
  } else {
    who = String(kind ?? DASH);
    sub = '';
  }
  if (target?.label) who = `${who} (${target.label})`;
  return { who, sub };
}

/** 마지막 발송({status, at, error} | null) → «보냄 · 2026-10-21 12:00» · 사유가 있으면 뒤에 붙인다. */
export function lastSendText(last) {
  if (!last || typeof last !== 'object') return '보낸 적 없음';
  let text = OUTBOX_STATUS_LABELS[last.status] || String(last.status ?? DASH);
  const at = fmtKstDateTime(last.at);
  if (at) text += ` · ${at}`;
  if (last.error) text += ` · ${SEND_ERROR_LABELS[last.error] || last.error}`;
  return text;
}

/** 시험 발송 응답 {ok, code} → 글. */
export function testResultText(result) {
  if (result?.ok === true) return '시험 발송 보냄';
  const code = result?.code;
  return `시험 발송 실패: ${SEND_ERROR_LABELS[code] || code || DASH}`;
}

/** 감사 1행 → {action, detail} 글. credentials 의 detail(key_id)은 그리지 않는다. */
export function auditText(entry) {
  const action = entry?.action;
  const d = entry?.detail && typeof entry.detail === 'object' ? entry.detail : {};
  if (action === 'enable') {
    return { action: '수집 켬', detail: `${d.reason || DASH}${d.was_enabled ? ' (이미 켜져 있었음)' : ''}` };
  }
  if (action === 'disable') return { action: '수집 끔', detail: d.reason || DASH };
  if (action === 'settings') {
    const changes = d.changes && typeof d.changes === 'object' ? d.changes : {};
    const fields = Array.isArray(d.fields) ? d.fields : Object.keys(changes);
    const parts = fields.map((f) => {
      const label = has(SETTINGS_LABELS, f) ? SETTINGS_LABELS[f] : String(f);
      const c = changes[f];
      if (!c || typeof c !== 'object') return label;
      return `${label}: ${fmtSettingValue(f, c.before, 60)} → ${fmtSettingValue(f, c.after, 60)}`;
    });
    return { action: '설정 변경', detail: parts.join(' · ') || DASH };
  }
  if (action === 'credentials') return { action: '계정 변경', detail: '네이버 계정 저장' };
  if (action === 'csv_export') {
    return { action: 'CSV 내려받기', detail: `${d.start || DASH}~${d.end || DASH} · ${isNum(d.rows) ? d.rows : DASH}행` };
  }
  const id = d.target_id ?? DASH;
  if (action === 'target_add') {
    return { action: '알림 대상 추가', detail: `#${id} · ${targetKindText(d.kind)} · ${kindsText(d.kinds)}` };
  }
  if (action === 'target_update') {
    // 서버 모양 {target_id, changes: {칸: {before, after}}} — 바뀐 칸 이름만 그린다.
    const changes = d.changes && typeof d.changes === 'object' ? d.changes : {};
    const names = Object.keys(changes).map((f) => (has(TARGET_FIELD_LABELS, f) ? TARGET_FIELD_LABELS[f] : String(f)));
    return { action: '알림 대상 수정', detail: [`#${id}`, ...names].join(' · ') };
  }
  if (action === 'target_delete') return { action: '알림 대상 삭제', detail: `#${id}` };
  if (action === 'test_send') {
    // 서버는 감사 행을 먼저 커밋하고 결과(ok·code)를 뒤에 더한다 — 결과가 아직 없을 수 있다.
    let result = ' · 결과 없음';
    if (d.ok === true) result = ' · 보냄';
    else if (d.ok === false) result = ` · 실패(${SEND_ERROR_LABELS[d.code] || d.code || DASH})`;
    return { action: '시험 발송', detail: `#${id}${result}` };
  }
  return { action: action ? String(action) : DASH, detail: DASH };
}
